import { forwardRef, useImperativeHandle, useRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

// Global Note wants a literal-pixel size picker (8/10/12/14/16/18/24px)
// instead of Quill's built-in small/normal/large/huge keywords (see
// GlobalNoteBox.jsx), so this registers a second, independent size format
// ("globalSize") under a style (inline font-size) attributor - keeping it
// a distinct format name (not reusing "size") avoids clobbering the Notes
// module's existing class-based small/false/large/huge whitelist, since
// Quill's format registry is a single global registry shared by every
// editor instance in the app.
const Parchment = Quill.import("parchment");
Quill.register(
  new Parchment.StyleAttributor("globalSize", "font-size", {
    scope: Parchment.Scope.INLINE,
    whitelist: ["8px", "10px", "12px", "14px", "16px", "18px", "24px"],
  }),
  true
);

const DEFAULT_TOOLBAR_OPTIONS = [
  [{ size: ["small", false, "large", "huge"] }],
  ["bold", "italic", "underline"],
  [{ list: "check" }, { list: "bullet" }],
];

const DEFAULT_FORMATS = ["size", "bold", "italic", "underline", "list"];

// Quill's getSemanticHTML() has a known gap: it exports checklist ("list:
// check") lines as plain <li> with no data-list attribute at all, silently
// collapsing checked/unchecked items into indistinguishable bullets (loses
// data on save). The Delta returned by editor.getContents() DOES keep the
// real list type per line, so we cross-reference it to re-inject the right
// data-list="checked"/"unchecked" attribute onto the matching <li> in the
// exported HTML, in document order (the only source of <li> elements is
// lines with a list attribute, so the two orderings line up 1:1).
function reinjectChecklistState(html, delta) {
  const listTypesInOrder = [];
  for (const op of delta.ops) {
    if (typeof op.insert === "string" && op.attributes?.list) {
      const lineBreaks = op.insert.match(/\n/g)?.length || 0;
      for (let i = 0; i < lineBreaks; i++) listTypesInOrder.push(op.attributes.list);
    }
  }
  if (!listTypesInOrder.some((type) => type === "checked" || type === "unchecked")) return html;
  const container = document.createElement("div");
  container.innerHTML = html;
  container.querySelectorAll("li").forEach((li, i) => {
    const type = listTypesInOrder[i];
    if (type === "checked" || type === "unchecked") li.setAttribute("data-list", type);
  });
  return container.innerHTML;
}

// Thin wrapper around react-quill-new. Defaults to the small formatting set
// the Notes text-record editor needs (font size, bold/italic/underline,
// numbered/bulleted/checklist lists); callers that need a fuller toolbar
// (e.g. the Global Note field) can override via `toolbarOptions`/`formats`.
// Runs uncontrolled (defaultValue) so typing never re-renders the parent;
// callers read the latest content imperatively via ref.getHtml() when they
// need to save, and can push AI-generated content back in via ref.setHtml().
export const RichTextEditor = forwardRef(function RichTextEditor({ defaultValue, placeholder, className, dataTestId, onChange, toolbarOptions, formats, toolbarContainer }, ref) {
  const quillRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getHtml: () => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return "";
      return reinjectChecklistState(editor.getSemanticHTML(), editor.getContents());
    },
    setHtml: (html) => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const delta = editor.clipboard.convert({ html });
      editor.setContents(delta, "silent");
    },
    // Used by the voice-input button: inserts transcribed text at the last
    // known cursor position (or the end of the document if the editor
    // lost focus, e.g. while the user was recording) and moves the cursor
    // to right after it, so consecutive ~1-minute chunks land in reading
    // order instead of all piling up at the same spot. Tagged "user" so
    // the existing onChange forwarding (which only reacts to real edits)
    // picks it up and the autosave loop persists it like any other typing.
    insertTextAtCursor: (text) => {
      const editor = quillRef.current?.getEditor();
      if (!editor || !text) return;
      const range = editor.getSelection();
      const index = range ? range.index : Math.max(0, editor.getLength() - 1);
      editor.insertText(index, text, "user");
      editor.setSelection(index + text.length, 0, "silent");
    },
    // Used by the "AI Emoji" button: reads back the plain-text of whatever
    // is currently highlighted, if anything (length 0 means no selection).
    getSelectedText: () => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return null;
      const range = editor.getSelection();
      if (!range || range.length === 0) return null;
      return { text: editor.getText(range.index, range.length), index: range.index, length: range.length };
    },
    // Replaces an exact [index, length) range (as returned by
    // getSelectedText above) with new plain text, then places the cursor
    // right after it. Tagged "user" for the same onChange/autosave reason
    // as insertTextAtCursor.
    replaceRange: (index, length, text) => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      editor.deleteText(index, length, "user");
      editor.insertText(index, text, "user");
      editor.setSelection(index + text.length, 0, "silent");
    },
    // Used when starting voice input on mobile: dismisses the on-screen
    // keyboard (blurring the contenteditable root) so it stops eating
    // screen space while the user is dictating instead of typing.
    blurEditor: () => {
      quillRef.current?.getEditor()?.root?.blur();
    },
  }));

  return (
    <div className={className} data-testid={dataTestId}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        defaultValue={defaultValue}
        placeholder={placeholder}
        modules={{ toolbar: toolbarContainer ? { container: toolbarContainer } : (toolbarOptions || DEFAULT_TOOLBAR_OPTIONS) }}
        formats={formats || DEFAULT_FORMATS}
        onChange={(content, delta, source, editor) => {
          // Only notify on user typing, not on our own programmatic setHtml()
          // calls (those are tagged "silent"/"api") - avoids redundant
          // autosave triggers when AI-organize content is applied.
          if (source === "user") {
            onChange?.(reinjectChecklistState(editor.getSemanticHTML(), editor.getContents()));
          }
        }}
      />
    </div>
  );
});
